import React, { useState, useCallback } from "react";
import "./VendorsTable.css";
import { Vendor } from "../../../types";
import {
  Table,
  Dropdown,
  type TableColumn,
  type DropdownOption,
} from "../../Common";
import { NewPagination } from "../../Common/Pagination/Pagination";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import { Box, Typography, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { VendorsListingService } from "../../../services";
import { useNavigate } from "react-router-dom";
import { useVendors } from "../../../hooks/useVendors";
import { useCategoryLabelMaps } from "../../../hooks/useCategoryLabelMaps";
import { formatCategoryLabel } from "../../../utils/categoryLabels";
import { CustomFeeModal } from "../../Vendors/CustomFeeModal";

interface VendorsTableProps {
  searchQuery?: string;
  filterStatus?: "active" | "suspended";
  onVendorSuspended?: () => void;
  onEditVendor?: (vendorId: string) => void;
}
const VendorsTable: React.FC<VendorsTableProps> = ({
  searchQuery = "",
  filterStatus,
  onVendorSuspended,
  onEditVendor,
}) => {
  const navigate = useNavigate();
  const { maps: categoryLabels } = useCategoryLabelMaps();

  // State for global default fee
  const [defaultFee, setDefaultFee] = useState<number>(5);

  // State for custom fee modal
  const [customFeeModalOpen, setCustomFeeModalOpen] = useState(false);
  const [selectedVendorForFee, setSelectedVendorForFee] = useState<Vendor | null>(null);

  // Normalize status to match backend format (Active, Suspended)
  const normalizedStatus = filterStatus 
    ? (filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1).toLowerCase()) as 'Active' | 'Suspended'
    : undefined;

  // Use the new useVendors hook with cursor pagination
  const {
    vendors,
    loading,
    currentPage,
    setCurrentPage,
    hasMore,
    totalPages,
    totalItems,
    pageStartIndex,
    pageEndIndex,
  } = useVendors({
    status: normalizedStatus,
    search: searchQuery,
    limit: 10,
  });

  // Fetch global default fee on mount
  React.useEffect(() => {
    const fetchDefaultFee = async () => {
      try {
        const response = await VendorsListingService.getDefaultFee();
        if (response?.defaultFeePercentage !== undefined) {
          setDefaultFee(response.defaultFeePercentage);
        }
      } catch (error) {
        console.error('Failed to fetch default fee:', error);
        // Keep default value of 5%
      }
    };
    fetchDefaultFee();
  }, []);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    vendor: Vendor
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedVendor(vendor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVendor(null);
  };

  const handleEdit = () => {
    if (selectedVendor && onEditVendor) {
      onEditVendor(selectedVendor.vendorId);
    }
    handleMenuClose();
  };

  const handleSuspend = async () => {
    if (!selectedVendor) return;

    try {
      // Use the new simplified endpoint
      await VendorsListingService.suspendActiveVendor(selectedVendor.vendorId);
      toast.success("Vendor suspended successfully");
      handleMenuClose();

      // Trigger refetch by calling the parent callback
      if (onVendorSuspended) {
        onVendorSuspended();
      }
      
      // Force page reload to refresh data
      window.location.reload();
    } catch (error: any) {
      console.error("Failed to suspend vendor:", error);
      toast.error(error?.response?.data?.message || "Failed to suspend vendor");
      handleMenuClose();
    }
  };

  const handleActivate = async () => {
    if (!selectedVendor) return;

    try {
      // Use the new simplified endpoint
      await VendorsListingService.activateSuspendedVendor(selectedVendor.vendorId);
      toast.success("Vendor activated successfully");
      handleMenuClose();

      // Trigger refetch by calling the parent callback
      if (onVendorSuspended) {
        onVendorSuspended();
      }
      
      // Force page reload to refresh data
      window.location.reload();
    } catch (error: any) {
      console.error("Failed to activate vendor:", error);
      toast.error(error?.response?.data?.message || "Failed to activate vendor");
      handleMenuClose();
    }
  };

  const fetchVendorsListing = useCallback(async (page = 1, searchQuery = "", status?: "active" | "suspended") => {
    // This function is no longer needed with the useVendors hook
    // Keeping it for backward compatibility but it won't be called
  }, []);

  // No longer needed - useVendors hook handles this
  // useEffect(() => {
  //   fetchVendorsListing(currentPage, searchQuery, filterStatus);
  // }, [currentPage, searchQuery, filterStatus, smartPageSize, fetchVendorsListing]);

  // No longer needed - useVendors hook handles this
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [searchQuery, filterStatus]);

  // Dropdown options - different options based on vendor status
  const getDropdownOptions = (vendor: Vendor | null): DropdownOption[] => {
    if (!vendor) return [];

    const options: DropdownOption[] = [];

    // Normalize status to lowercase for comparison
    const vendorStatus = vendor.status?.toLowerCase();

    console.log('🔍 Vendor status for menu:', {
      vendorId: vendor.vendorId,
      originalStatus: vendor.status,
      normalizedStatus: vendorStatus
    });

    // For InReview status, show both Activate and Suspend options
    if (vendorStatus === 'inreview') {
      options.push({
        label: "Activate",
        value: "activate",
        onClick: handleActivate,
      });
      options.push({
        label: "Suspend",
        value: "suspend",
        onClick: handleSuspend,
      });
    } else if (vendorStatus === 'suspended') {
      // Show only "Activate" for suspended vendors
      options.push({
        label: "Activate",
        value: "activate",
        onClick: handleActivate,
      });
    } else {
      // Default to showing "Suspend" for active or any other status
      options.push({
        label: "Suspend",
        value: "suspend",
        onClick: handleSuspend,
      });
    }

    // Always show "Change fee amount" option
    options.push({
      label: "Change fee amount",
      value: "changeFee",
      onClick: () => {
        setSelectedVendorForFee(vendor);
        setCustomFeeModalOpen(true);
        handleMenuClose();
      },
    });

    return options;
  };

  const columns: TableColumn<Vendor>[] = [
    {
      key: "id",
      label: "ID",
      width: 1,
      render: (vendor: Vendor) => {
        const shortId = vendor.vendorId.slice(-3).toUpperCase();
        const displayId = `...${shortId}`;
        return (
          <Box className="vendors-table-id-cell">
            <Typography>{displayId}</Typography>
          </Box>
        );
      },
    },
    {
      key: "vendor",
      label: "Vendor",
      width: 5,
      render: (vendor: Vendor) => (
        <Box className="vendors-table-vendor-cell">
          {/* Business Image */}
          <Box
            component="img"
            src={vendor.profileImageUrl}
            alt={vendor.companyName}
            className="vendors-table-vendor-image-cell"
          />

          {/* Business Info (name + email) */}
          <Box className="vendors-table-vendor-info-cell">
            <Typography className="vendors-table-vendor-name-cell">
              {vendor.companyName}
            </Typography>
            <Typography className="vendors-table-vendor-email-cell">
              {vendor.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: "services",
      label: "Services",
      width: 5,
      render: (vendor: Vendor) => {
        if (!vendor.services || vendor.services.length === 0) {
          return (
            <Box className="vendors-table-services-cell">
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                No services
              </Typography>
            </Box>
          );
        }
        
        return (
          <Box className="vendors-table-services-cell">
            {vendor.services.map((service, serviceIndex) => {
              const typeKey = service.type || 'Other';
              const type = formatCategoryLabel(typeKey, categoryLabels?.typeByKey || {}) || typeKey;
              const items = service.items || [];
              return (
                <Box key={serviceIndex} sx={{ marginBottom: serviceIndex < vendor.services!.length - 1 ? '4px' : 0 }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    <Typography component="span" sx={{ fontWeight: 700 }}>
                      {type}
                    </Typography>
                    {items.length > 0 && (
                      <Typography component="span" sx={{ fontWeight: 400 }}>
                        {': '}{items.join(', ')}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      key: "orders",
      label: "Orders",
      width: 1,
      render: (vendor: Vendor) => {
        const ordersCount = vendor.orderCount || vendor.totalOrders || 0;
        return (
          <Box className="vendors-table-orders-cell">
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
              {ordersCount}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "eventiFee",
      label: "Eventi Fee",
      width: 2,
     render: (vendor: Vendor) => {
      // Display logic: If customFeePercentage !== 0, show custom fee; else show global default
      const customFee = vendor.customFeePercentage;
      const displayFee = customFee !== undefined && customFee !== 0 
    ? `${customFee}%` 
    : `${defaultFee}%`;
      const isCustom = customFee !== undefined && customFee !== 0;

        return (
          <Box 
            className="vendors-table-eventi-fee-cell"
            onClick={() => {
              setSelectedVendorForFee(vendor);
              setCustomFeeModalOpen(true);
            }}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              }
            }}
          >
            <Typography sx={{ 
              fontSize: 'var(--palette-light-font-size-default-value)', 
              color: isCustom ? 'primary.main' : 'var(--palette-light-text-primary-value-hex)',
              fontWeight: isCustom ? 600 : 400,
            }}>
              {displayFee}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "earnings",
      label: "Earnings",
      width: 1,
      render: (vendor: Vendor) => {
        const earnings = vendor.earnings || vendor.totalEarnings || 0;
        return (
          <Box className="vendors-table-earnings-cell">
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
              QAR {earnings.toFixed(2)}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "actions",
      label: "",
      width: "56px",
      align: "center",
      render: (vendor) => (
        <IconButton
          className="vendors-table-action-button"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from triggering
            handleMenuOpen(e, vendor);
          }}
        >
          <MoreHorizIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="vendors-table-wrapper">
      <Table
        onRowClick={(vendor) => navigate(`/vendors/${vendor.vendorId}`)}
        columns={columns}
        data={vendors}
        keyExtractor={(vendor) => vendor.vendorId}
        emptyMessage="No vendors found"
        loading={loading}
      />

      {/* Pagination - Use NewPagination component matching Orders page */}
      {vendors.length > 0 && (
        <NewPagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={hasMore || currentPage < totalPages}
          onPageChange={setCurrentPage}
          itemsMessage={`${pageStartIndex}–${pageEndIndex} of ${totalItems}${hasMore ? '+' : ''} vendors`}
        />
      )}

      {/* Action Dropdown */}
      <Dropdown
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        options={getDropdownOptions(selectedVendor)}
      />

      {/* Custom Fee Modal */}
      {selectedVendorForFee && (
        <CustomFeeModal
          open={customFeeModalOpen}
          onClose={() => {
            setCustomFeeModalOpen(false);
            setSelectedVendorForFee(null);
          }}
          vendor={{
            vendorId: selectedVendorForFee.vendorId,
            companyName: selectedVendorForFee.companyName || 'Unknown Vendor',
            customFeePercentage: selectedVendorForFee.customFeePercentage,
          }}
          defaultFee={defaultFee}
          onSuccess={() => {
            toast.success('Custom fee updated successfully');
            // Force a full page reload to refresh the vendors list
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }}
        />
      )}
    </Box>
  );
};

export default VendorsTable;
